import React, { useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Play, RotateCcw, AlertTriangle, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import RiskGauge from "@/components/command/RiskGauge";

export const WhatIfPanel = ({ zones = [] }) => {
  const [closedGates, setClosedGates] = useState(new Set());
  const [evacZones, setEvacZones] = useState(new Set());
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const gates = useMemo(() => zones.filter((z) => z.type === "gate"), [zones]);
  const stands = useMemo(() => zones.filter((z) => z.type === "stand").slice(0, 8), [zones]);

  const toggle = (setFn) => (id) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const run = async () => {
    setRunning(true);
    try {
      const r = await http.post("/predictive/whatif", {
        closed_gates: [...closedGates],
        evacuate_zones: [...evacZones],
      });
      setResult(r.data);
    } catch {
      toast.error("What-if failed");
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setClosedGates(new Set());
    setEvacZones(new Set());
    setResult(null);
  };

  const delta = result?.delta ?? 0;
  const deltaPct = Math.round(Math.abs(delta) * 100);
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div className="panel p-3 space-y-3" data-testid="whatif-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 sev-medium" />
          <span className="h-widget">What-If Simulator</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={reset} className="btn-tac !px-2 !py-1" data-testid="whatif-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button onClick={run} className="btn-tac-primary !px-2 !py-1" data-testid="whatif-run-btn" disabled={running}>
            <Play className="w-3 h-3" /> {running ? "Computing…" : "Run Simulation"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Gate closures */}
        <div className="panel-elevated p-3">
          <div className="micro-label mb-2">Close Gates</div>
          <div className="space-y-1.5">
            {gates.map((g) => (
              <label key={g.id} className="flex items-center justify-between gap-2 cursor-pointer" data-testid={`whatif-gate-${g.id}`}>
                <span className="font-mono text-xs truncate">{g.name}</span>
                <Switch
                  checked={closedGates.has(g.id)}
                  onCheckedChange={() => toggle(setClosedGates)(g.id)}
                />
              </label>
            ))}
            {gates.length === 0 && <div className="text-xs text-muted-foreground">No gates available.</div>}
          </div>
        </div>

        {/* Evacuate zones */}
        <div className="panel-elevated p-3">
          <div className="micro-label mb-2">Evacuate Zones</div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {stands.map((z) => (
              <label key={z.id} className="flex items-center justify-between gap-2 cursor-pointer" data-testid={`whatif-evac-${z.id}`}>
                <span className="font-mono text-xs truncate">{z.name}</span>
                <Switch
                  checked={evacZones.has(z.id)}
                  onCheckedChange={() => toggle(setEvacZones)(z.id)}
                />
              </label>
            ))}
          </div>
        </div>

        {/* Result */}
        <div className="panel-elevated p-3 flex flex-col">
          <div className="micro-label mb-2">Result</div>
          {!result && (
            <div className="text-xs text-muted-foreground">
              Toggle constraints on the left and click <em>Run Simulation</em> to see the projected impact on the Panic Risk Index.
            </div>
          )}
          {result && (
            <>
              <div className="flex items-center gap-3">
                <RiskGauge value={result.simulated.panic_index} size={88} label="Simulated" />
                <div className="min-w-0">
                  <div className={`flex items-center gap-1 font-display font-black uppercase tracking-tight text-sm ${direction === "up" ? "sev-critical" : direction === "down" ? "sev-low" : "text-muted-foreground"}`}>
                    {direction === "up" && <ArrowUpRight className="w-4 h-4" />}
                    {direction === "down" && <ArrowDownRight className="w-4 h-4" />}
                    Δ {delta > 0 ? "+" : ""}{deltaPct} pts
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                    baseline {Math.round((result.baseline.panic_index ?? 0) * 100)} → simulated {Math.round((result.simulated.panic_index ?? 0) * 100)}
                  </div>
                  <div className="font-display font-bold uppercase tracking-tight text-[11px] mt-1">
                    {result.simulated.level}
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-[11px] font-mono">
                {result.explain.map((e, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-muted-foreground">·</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatIfPanel;
